import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';
import { fromIso9075DateString, getRewardTitle } from 'utils/themeUtils';

const PATRON_REWARDS_QUERY = gql`
  query hotelRewardsWithTimeZone($input: GetHotelRewardsWithTimezone!) {
    hotelRewardsWithTimeZone(input: $input) {
      rewardTimeZoneHelpText
      patronRewards {
        prizeCode
        prizeType
        prizeName
        startDate
        endDate
        startDateUTC
        endDateUTC
        redeemed
        maxPrizesPerPeriod
        isNewOffer
        isSoftOffer
      }
    }
  }
`;

const REDEEM_ONLINE_OFFER = gql`
  mutation redeemOnlineOffer($input: RedeemOnlineOfferInput!) {
    redeemOnlineOffer(input: $input) {
      offerUuid
      success
      message
    }
  }
`;

const RESET_NEW_OFFERS = gql`
  mutation resetNew {
    removeNewOffers
  }
`;

//  ---------------------------------

const getTimeClass = function(now = new Date()) {
  const warningMs = 7 * 86400000; //  week
  if (this.endDate < now) {
    return 'expired';
  }
  if (this.startDate < now) {
    return this.endDate.getTime() - warningMs < now.getTime()
      ? 'running'
      : 'expiring';
  }
  return 'pending';
};

const isRedeemed = function() {
  return this.type === 'WStoreEarlyAccess'
    ? this.redeemed
    : this.redeemed >= this.quantity;
};

const isExpired = function(now = new Date()) {
  return !this.isRedeemed() && now >= this.endDate;
};

const isAvailable = function(now = new Date()) {
  return !this.isRedeemed() && !this.isExpired(now);
};

const showRedeemBtn = function(now = new Date()) {
  return this.isAvailable(now);
};

const nope = () => false;

const getStatus = function(now = new Date()) {
  if (this.isRedeemed()) {
    return 'Redeemed';
  }
  if (this.isExpired(now)) {
    return 'Expired';
  }
  return '';
};

export default compose(
  graphql(PATRON_REWARDS_QUERY, {
    options: props => {
      const { submittedAccount } = props;

      let input = {};
      if (submittedAccount?.accountNumber) {
        input.patronAccountNumber = submittedAccount.accountNumber;
      }

      if (submittedAccount?.accountPin) {
        input.pin = submittedAccount.accountPin;
      }

      return {
        fetchPolicy: 'network-only',
        variables: { input }
      };
    },
    skip: props =>
      !props.authenticatedNonAdmin &&
      (!props.submittedAccount?.accountNumber ||
        (!props.submittedAccount?.accountPin && !props.isAdmin)),
    props: ({ data, ownProps }) => {
      const rewardsWithTimeZone = data.hotelRewardsWithTimeZone;
      const rewards = rewardsWithTimeZone
        ? rewardsWithTimeZone.patronRewards
        : [];
      const rewardTimeZoneHelpText = rewardsWithTimeZone
        ? rewardsWithTimeZone.rewardTimeZoneHelpText
        : '';
      const patronRewards = [];
      const onlineRewards = [];

      rewards.forEach(reward => {
        const r = {
          rewardId: reward.prizeCode,
          isSoftOffer: reward.isSoftOffer,
          name: getRewardTitle(reward.prizeType),
          type: reward.prizeType,
          quantity: reward.maxPrizesPerPeriod,
          redeemed: reward.redeemed,
          description: reward.prizeName,
          isNew: reward.isNewOffer,
          getTimeClass,
          isAvailable: isAvailable,
          isRedeemed: isRedeemed,
          isExpired: isExpired,
          showRedeemBtn: showRedeemBtn,
          getStatus: getStatus,
          startDateDisplay: reward.startDate,
          endDateDisplay: reward.endDate,
          startDate: fromIso9075DateString(reward.startDateUTC),
          endDate: fromIso9075DateString(reward.endDateUTC)
        };
        if (reward.isSoftOffer) {
          //  soft offer
          onlineRewards.push(r);
        } else {
          //  patron reward
          r.showRedeemBtn = nope;
          patronRewards.push(r);
        }
      });
      patronRewards.sort((lhs, rhs) => rhs.endDate - lhs.endDate);
      onlineRewards.sort((lhs, rhs) => rhs.endDate - lhs.endDate);

      return {
        rewardsData: data,
        patronRewards,
        onlineRewards,
        rewardTimeZoneHelpText
      };
    }
  })
);
